<!--
  Phase 01.1 Plan 07：AMOE render 主入口。
  - DEBUG_USE_MOCK 常量 + URL `?mockScenario=` 切 mock / 真 apiClient（D-39；Phase 2 切真改为 false）
  - render 同文件静态 import createApiClient + createMockApiClient（用户决策：不用 import.meta.env.DEV / dynamic import / async bootstrap）
  - composable 编排：fields / sms / availability / lottery
  - Page gate：seasonSign 空 → 全屏空态（D-35），seasonSign 非空 → onMounted 拉 availability 一次（D-34）
  - NoticeModal 按 lottery.state.kind 映射（9+1 kind，D-14 + D-04）
  - TOS/Privacy 链接 emit 到外部宿主（D-19）
  - 硬约束 #7：不使用全屏 loading toast；loading 态由 NoticeModal deliveryLoading 承载
-->
<template>
  <div
    class="amoe-render"
    :class="{ isPC: pageType === 'PC' }"
  >
    <!-- Page gate：seasonSign 未配置 → 全屏空态（D-35） -->
    <div
      v-if="!seasonSign"
      class="empty-state"
    >
      <h2 class="empty-title">{{ t('amoe.result.seasonSignMissing.title') }}</h2>
      <p class="empty-msg">{{ t('amoe.result.seasonSignMissing.message') }}</p>
    </div>

    <AmoeUserForm
      v-else
      :page-type="pageType"
      :season-sign="seasonSign"
      :agreement-checked="agreementChecked"
      :fields="fields"
      :sms="sms"
      :lottery="lottery"
      :availability="availability"
      @submit="onSubmit"
      @send-sms="onSendSms"
      @update:agreement-checked="agreementChecked = $event"
      @agreement:tos-click="onTosClick"
      @agreement:privacy-click="onPrivacyClick"
    />

    <NoticeModal
      v-if="noticeModalProps.visible"
      :visible="true"
      :page-type="pageType"
      :variant="noticeModalProps.variant"
      :image-src="noticeModalProps.imageSrc"
      :image-alt="noticeModalProps.imageAlt"
      :image-preset="noticeModalProps.imagePreset"
      :title="noticeModalProps.title"
      :description="noticeModalProps.description"
      :button-text="noticeModalProps.buttonText"
      :show-close-button="noticeModalProps.showCloseButton"
      :close-on-mask="noticeModalProps.closeOnMask"
      @confirm="onModalConfirm"
      @close="onModalClose"
    />

    <DrawResultPopup
      :visible="showDrawResultPopup"
      :draw-result="drawResultForPopup"
      :prize-configs="prizeConfigs"
      :is-p-c="pageType === 'PC'"
      @brag="onDrawResultBrag"
      @close="onDrawResultClose"
    />

    <!-- 复用 flash-sale 的排队提示弹窗；pending 终态不再展示 still drawing 文案。 -->
    <InLinePopup
      :visible="showInLine"
      :is-p-c="pageType === 'PC'"
      @close="onDismissInLine"
    />

    <!-- flash-sale 共用友好提示弹窗：承载无抽奖机会等业务提示。 -->
    <FriendlyPromptPopup
      :visible="!!friendlyPromptType"
      :type="friendlyPromptType"
      :is-p-c="pageType === 'PC'"
      @close="onFriendlyPromptClose"
    />

    <!-- 已登录用户访问 AMOE 页面时的提示弹窗 -->
    <div v-if="showLoggedInPopup" class="logged-in-popup__overlay" :style="{ zIndex: loggedInPopupZIndex }">
      <div :class="['logged-in-popup', pageType === 'PC' ? 'is-pc' : 'is-h5']">
        <div class="logged-in-popup__illust">
          <img class="logged-in-popup__icon" :src="moomooIconSrc" alt="moomoo" />
          <div class="logged-in-popup__divider" />
        </div>
        <div class="logged-in-popup__body">
          <h3 class="logged-in-popup__title">Already Logged In</h3>
          <p class="logged-in-popup__desc">
            You are currently logged into your moomoo account. Please return to the event page to participate, or use an email and phone number not registered with moomoo to join via the AMOE page
          </p>
        </div>
        <div class="logged-in-popup__actions">
          <button class="logged-in-popup__btn logged-in-popup__btn--primary" style="flex-grow: 3" @click="onGoEventPage">
            Go to Event Page
          </button>
          <button class="logged-in-popup__btn logged-in-popup__btn--outline" style="flex-grow: 2" @click="onDismissLoggedIn">
            Got It
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue-demi';
import type { PropType } from 'vue-demi';
import {
  NoticeModal,
  FriendlyPromptPopup,
  InLinePopup,
  safeOpen,
  moomooIconSrc,
  bookIconSrc,
} from '@ark/mets-frontend-shared';
import type { FlashSalePromptType } from '@ark/mets-frontend-shared';
import {
  useFormFields,
  useSmsCountdown,
  useAvailability,
  useLottery,
} from '../composables';
import { AmoeUserForm } from '../components';
import {
  createApiClient,
  type AmoeApiClient,
} from '../shared/api';
import type { PrizeConfig } from '../shared/types';
import { use } from '@fegobull/webapi';
import { DRAW_STATUS } from '@ark/mets-shared';
import t from './utils/i18n';
import DrawResultPopup from './components/DrawResultPopup.vue';

// FEGO runtime hooks
const { usePageType, useToast, useUser, useZIndex } = use;

type NoticeVisibleProps = {
  visible: true;
  variant: 'notice' | 'confirm' | 'alert';
  imageSrc?: string;
  imageAlt?: string;
  imagePreset?: 'default' | 'moomoo' | 'book';
  title: string;
  description: string;
  buttonText: string;
  showCloseButton: boolean;
  closeOnMask: boolean;
};

type NoticeHiddenProps = { visible: false };

export default defineComponent({
  name: 'AmoeUserFormRender',
  components: {
    AmoeUserForm,
    NoticeModal,
    FriendlyPromptPopup,
    InLinePopup,
    DrawResultPopup,
  },
  props: {
    seasonSign: { type: String, default: '' },
    tosUrl: { type: String, default: '' },
    privacyUrl: { type: String, default: '' },
    homeUrl: { type: String, default: '' },
    /** editor 传入的奖品配置列表，用于中奖结果弹窗素材匹配。 */
    prizes: {
      type: Array as PropType<PrizeConfig[]>,
      default: () => [],
    },
  },
  emits: ['agreement:tos-click', 'agreement:privacy-click', 'submit-success'],
  setup(props, { emit }) {
    const { isPC } = usePageType() as any;
    const { isLogin } = useUser() as any;
    const pageType = computed<'PC' | 'H5'>(() => {
      const isPcValue = isPC && typeof isPC === 'object' && 'value' in isPC ? isPC.value : isPC;
      return isPcValue ? 'PC' : 'H5';
    });

    /** 已登录用户访问 AMOE 页面时弹出提示 */
    const showLoggedInPopup = ref(false);
    const loggedInPopupZIndex = useZIndex();
    /** flash-sale 共用友好提示弹窗类型 */
    const promptType = ref<FlashSalePromptType | null>(null);

    /** 排队提示弹窗可见态：提交后的处理中与结果未收敛都复用同一视觉。 */
    const showInLine = computed(() =>
      (lottery.state.value.kind === 'deliveryLoading' || lottery.state.value.kind === 'pending') &&
      !dismissedInLine.value,
    );
    const Toast = useToast() as any;
    /** 业务错误需要足够长的阅读时间，特别是验证码过期/次数超限场景。 */
    const ERROR_TOAST_DURATION_MS = 4500;

    const apiClient: AmoeApiClient = createApiClient();
    const prizeConfigs = computed<PrizeConfig[]>(() =>
      (props.prizes ?? []).map((p) => ({
        prizeId: Number(p.prizeId),
        isGrandPrize: !!p.isGrandPrize,
        prizeAsset: p.prizeAsset ?? '',
      })),
    );
    const drawResultForPopup = computed(() => {
      const s = lottery.state.value;
      if (s.kind !== 'won') return null;
      return {
        drawStatus: DRAW_STATUS.WON,
        prize: s.prize,
      };
    });
    const showDrawResultPopup = computed(() => drawResultForPopup.value !== null);
    const friendlyPromptType = computed<FlashSalePromptType | null>(() => {
      if (promptType.value) return promptType.value;
      if (lottery.state.value.kind === 'noEntries') return 'no_entries';
      return null;
    });

    const fields = useFormFields({ defaultCountryCode: '+1' });
    const sms = useSmsCountdown();
    const availability = useAvailability(apiClient);
    const lottery = useLottery(apiClient, {
      fields,
      sms,
      getSeasonSign: () => props.seasonSign,
      // 8-point matrix #7：loading 态由 NoticeModal deliveryLoading 承载，不用全屏 loading toast
      onToast: (msg: string) =>
        Toast.error({
          message: msg,
          duration: ERROR_TOAST_DURATION_MS,
          zIndex: useZIndex(),
        }),
    });
    const agreementChecked = ref(false);
    /** 用户已关闭 "You're in" 弹窗，polling 继续后台运行 */
    const dismissedInLine = ref(false);

    onMounted(async () => {
      // 已登录用户访问 AMOE 时弹窗提示回主活动页
      if (isLogin) {
        showLoggedInPopup.value = true;
      }
      // D-34：mount 拉一次 availability；seasonSign 空不发网络请求
      if (props.seasonSign.length > 0) {
        await availability.fetch(props.seasonSign);
        if (!availability.hasActiveRound.value && !availability.error.value) {
          promptType.value = 'waiting_for_win';
        }
      }
    });

    async function onSubmit(): Promise<void> {
      dismissedInLine.value = false;
      const loadingToken = Toast.loading({
        duration: 0,
        message: 'Entering the draw...',
        zIndex: useZIndex(),
      });
      await lottery.submit();
      // submit 内部错误走 onToast(Toast.error) 自动替换 loading；
      // 成功/noRound 路径手动关 loading，避免 close 误杀 error toast。
      const kind = lottery.state.value.kind;
      if (kind === 'noRound') {
        promptType.value = 'waiting_for_win';
        lottery.reset();
      }
      if (kind !== 'idle') {
        Toast.close(loadingToken);
      }
    }

    function onSendSms(): void {
      void lottery.sendSms();
    }

    function onTosClick(): void {
      // D-19：协议跳转由宿主处理，组件只上抛点击事件。
      emit('agreement:tos-click');
    }

    function onPrivacyClick(): void {
      // D-19：协议跳转由宿主处理，组件只上抛点击事件。
      emit('agreement:privacy-click');
    }

    // NoticeModal props computed 按 lottery.state.kind 映射（9+1 kind，D-14）
    // - idle / submitting / noRound / pending → visible=false（pending 复用 InLinePopup）
    // - deliveryLoading：超时自动切 pending 由 Plan 04 useLottery 驱动，render 层仅消费 state（RH-B3）
    const noticeModalProps = computed<NoticeVisibleProps | NoticeHiddenProps>(
      () => {
        const s = lottery.state.value;
        switch (s.kind) {
          case 'idle':
          case 'submitting':
          case 'noRound':
          case 'pending':
          case 'noEntries':
            return { visible: false };
          case 'deliveryLoading':
            // 由 InLinePopup 承载，NoticeModal 不展示
            return { visible: false };
          case 'won':
            // 中奖结果改由 DrawResultPopup 承载，保持与 flash-sale-status 一致。
            return { visible: false };
          case 'lost':
            return {
              visible: true,
              variant: 'notice',
              title: t('amoe.result.lost.title'),
              description: t('amoe.result.lost.message'),
              buttonText: t('amoe.result.lost.cta'),
              showCloseButton: true,
              closeOnMask: true,
            };
          case 'alreadyMoomoo':
            return {
              visible: true,
              variant: 'notice',
              imageSrc: moomooIconSrc,
              imageAlt: 'moomoo',
              imagePreset: 'moomoo',
              title: t('amoe.result.alreadyMoomoo.title'),
              description: t('amoe.result.alreadyMoomoo.message'),
              buttonText: t('amoe.result.alreadyMoomoo.cta'),
              showCloseButton: true,
              closeOnMask: true,
            };
          case 'duplicate':
            return {
              visible: true,
              variant: 'notice',
              imageSrc: bookIconSrc,
              imageAlt: 'entry record',
              imagePreset: 'book',
              title: t('amoe.result.duplicate.title'),
              description: t('amoe.result.duplicate.message'),
              buttonText: t('amoe.result.duplicate.cta'),
              showCloseButton: true,
              closeOnMask: true,
            };
          case 'ipRejected':
            return {
              visible: true,
              variant: 'notice',
              imageSrc: moomooIconSrc,
              imageAlt: 'moomoo',
              imagePreset: 'moomoo',
              title: t('amoe.result.ipRejected.title'),
              description: t('amoe.result.ipRejected.message'),
              buttonText: t('amoe.result.ipRejected.cta'),
              showCloseButton: true,
              closeOnMask: true,
            };
        }
      },
    );

    function onModalConfirm(): void {
      const s = lottery.state.value;
      if (s.kind === 'deliveryLoading') {
        // "Got it" 关闭 "You're in" 弹窗，不中断 polling
        dismissedInLine.value = true;
        return;
      }
      if (s.kind === 'won') {
        if (props.homeUrl.length > 0) {
          safeOpen(props.homeUrl);
        }
        emit('submit-success', { drawStatus: 1, prize: s.prize });
      }
      lottery.reset();
    }

    function onModalClose(): void {
      if (lottery.state.value.kind === 'deliveryLoading') {
        dismissedInLine.value = true;
        return;
      }
      lottery.reset();
    }

    function onDrawResultBrag(): void {
      const s = lottery.state.value;
      if (s.kind !== 'won') return;

      if (props.homeUrl.length > 0) {
        safeOpen(props.homeUrl);
      }
      emit('submit-success', { drawStatus: 1, prize: s.prize });
      lottery.reset();
    }

    function onDrawResultClose(): void {
      lottery.reset();
    }

    function onFriendlyPromptClose(): void {
      promptType.value = null;
      if (lottery.state.value.kind === 'noEntries') {
        lottery.reset();
      }
    }

    function onDismissInLine(): void {
      if (lottery.state.value.kind === 'pending') {
        lottery.reset();
        return;
      }
      dismissedInLine.value = true;
    }

    function onGoEventPage(): void {
      if (props.homeUrl.length > 0) {
        safeOpen(props.homeUrl);
      }
      showLoggedInPopup.value = false;
    }

    function onDismissLoggedIn(): void {
      showLoggedInPopup.value = false;
    }

    return {
      pageType,
      fields,
      sms,
      availability,
      lottery,
      agreementChecked,
      showLoggedInPopup,
      loggedInPopupZIndex,
      noticeModalProps,
      showDrawResultPopup,
      drawResultForPopup,
      prizeConfigs,
      friendlyPromptType,
      onSubmit,
      onSendSms,
      onTosClick,
      onPrivacyClick,
      onModalConfirm,
      onModalClose,
      onDrawResultBrag,
      onDrawResultClose,
      onFriendlyPromptClose,
      onGoEventPage,
      onDismissLoggedIn,
      onDismissInLine,
      showInLine,
      promptType,
      moomooIconSrc,
      t,
    };
  },
});
</script>

<style scoped>
.amoe-render {
  min-height: 100vh;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: #ffffff;
  text-align: center;
  padding: 0 0.4rem;
  box-sizing: border-box;
}

.empty-title {
  font-size: 0.36rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
}

.empty-msg {
  font-size: 0.28rem;
  line-height: 1.5;
}

/* PC: 大写 PX 用于跳过 postcss-pxtorem，避免空态固定尺寸被转成 rem 后受根字号影响 */
.amoe-render.isPC .empty-title {
  font-size: 28PX;
  font-weight: 600;
  margin-bottom: 12PX;
}

.amoe-render.isPC .empty-msg {
  font-size: 18PX;
  line-height: 1.5;
}

/* ─── Already Logged In Popup ────────────────────────────── */
.logged-in-popup__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.logged-in-popup {
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  font-family: BR Hendrix, Arial, sans-serif;
}

.logged-in-popup__illust {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.logged-in-popup__icon {
  display: block;
  object-fit: contain;
}

.logged-in-popup__divider {
  width: 100%;
}

.logged-in-popup__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;
}

.logged-in-popup__title {
  font-family: MiSans, BR Hendrix, Arial, sans-serif;
  font-weight: 520;
  color: #0f1112;
  margin: 0;
}

.logged-in-popup__desc {
  color: #525252;
  margin: 0;
  line-height: 1.4;
}

.logged-in-popup__actions {
  display: flex;
  width: 100%;
}

.logged-in-popup__btn {
  border: none;
  font-family: BR Hendrix, Arial, sans-serif;
  font-weight: 600;
  cursor: pointer;
  border-radius: 200px;
  text-align: center;
}

.logged-in-popup__btn--primary {
  background: #ff6900;
  color: #ffffff;
}

.logged-in-popup__btn--outline {
  background: #ffffff;
  color: #ff6900;
  border: 1px solid #ff6900;
}

/* ─── H5: 设计稿 100px = 1rem ────────────── */
.logged-in-popup.is-h5 {
  width: 6.7rem;
  border-radius: 0.32rem;
  padding: 0.6rem 0.4rem 0.4rem;
  gap: 0.6rem;
}

.logged-in-popup.is-h5 .logged-in-popup__icon {
  width: auto;
  max-height: 2.77rem;
  margin-bottom: 0.2rem;
}

.logged-in-popup.is-h5 .logged-in-popup__body {
  gap: 0.24rem;
}

.logged-in-popup.is-h5 .logged-in-popup__title {
  font-size: 0.36rem;
}

.logged-in-popup.is-h5 .logged-in-popup__desc {
  font-size: 0.3rem;
}

.logged-in-popup.is-h5 .logged-in-popup__actions {
  gap: 0.2rem;
}

.logged-in-popup.is-h5 .logged-in-popup__btn {
  flex: 1;
  height: 0.9rem;
  font-size: 0.36rem;
  line-height: 0.9rem;
  padding: 0;
}

/* ─── PC ────────────────────────────── */
.logged-in-popup.is-pc {
  width: 480px;
  border-radius: 24px;
  padding: 40px 28px 28px;
  gap: 40px;
}

.logged-in-popup.is-pc .logged-in-popup__icon {
  width: auto;
  max-height: 1.66rem;
  margin-bottom: 12px;
}

.logged-in-popup.is-pc .logged-in-popup__body {
  gap: 16px;
}

.logged-in-popup.is-pc .logged-in-popup__title {
  font-size: 24px;
}

.logged-in-popup.is-pc .logged-in-popup__desc {
  font-size: 16px;
}

.logged-in-popup.is-pc .logged-in-popup__actions {
  gap: 12px;
}

.logged-in-popup.is-pc .logged-in-popup__btn {
  flex: 1;
  height: 56px;
  font-size: 20px;
  line-height: 56px;
  padding: 0;
}
</style>
